<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/solid';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import BulkEditDialog from './BulkEditDialog.vue';
import ScheduleBulkEditDialog, { type BulkEditPayload } from './ScheduleBulkEditDialog.vue';
import BulkTagDialog from './BulkTagDialog.vue';
import BulkConfirmDialog from './BulkConfirmDialog.vue';
import BulkRunReport from './BulkRunReport.vue';
import { describeBulkChange } from './bulkEditChanges';
import { twoKindSelectionText, type BulkBarScheduleSelection } from './bulkSelectionText';
import type { BulkSelectionMode, BulkSelectionPayload } from '@/composables/useBulkSelection';
import {
  toWithoutListsSelection,
  useBulkCapabilities,
  type BulkOperation,
  type BulkRequestSelection,
  type BulkRun,
} from '@/composables/api/useBulkActionApi';
import {
  BULK_RUN_CATCH_UP_NOTE,
  bulkRunDoneSummary,
  bulkRunFailedCount,
  useBulkActionRun,
} from '@/composables/useBulkActionRun';
import { useMixedBulkRun, type MixedRunKind, type MixedRunPart } from '@/composables/useMixedBulkRun';
import type { SchedulesBeforeRun } from '@/composables/api/bulkActionCachePatch';
import { useAuthStore } from '@/stores/authStore';

/**
 * The bar above the search results for acting on the selected schedules or custom lists.
 *
 * It reads "N selected", "All N matching", or "N of M matching" once rows are unticked, then offers Bulk edit, Delete, Add tag and Remove tag.
 * Next to "N selected" it offers "Select all N matching" in small text, from the first ticked row.
 * The capabilities endpoint decides which of those this user sees. Above the row cap the actions
 * are disabled, and the bar says to narrow the search.
 *
 * Once a run is started the bar shows its progress, then a summary with a link to the report. A
 * page reload does not lose the run. That is why the bar is mounted for the whole bulk-selectable
 * page, and draws itself only when there is a selection or a run to show.
 *
 * On the custom list search the page passes `schedules`, because schedules can be ticked there too.
 * The bar then says what is selected of each kind, such as "3 lists, 14 schedules selected". Its
 * offer reads "Select all matching 40 custom lists / 112 schedules", and each count is its own link.
 *
 * There, Bulk edit and Delete act on both kinds. Each kind's share needs that model's ability,
 * read from its own capabilities, so a user who may delete lists but not schedules deletes only
 * the ticked lists. Add tag and Remove tag act on lists only, and are hidden when no list is
 * ticked. With both kinds in an action, `useMixedBulkRun` runs it as two runs: the schedules
 * first, then the lists. A delete of both sends the list selection as the schedule selection's
 * `without_lists`, so a schedule on a deleted list is never counted twice. Once an action starts,
 * the bar emits `clear` with the kinds it covered, and the page clears those selections.
 *
 * Every vue-query call for bulk actions lives in this bar and its dialogs, never in the search
 * page's own setup. Some search page tests mount it without a query client.
 */
const props = withDefaults(defineProps<{
  entitySlug: string;
  mode: BulkSelectionMode;
  count: number;
  /** Rows unticked after "select all matching". */
  excludedCount?: number;
  /** The pager's total for the search on screen. */
  total?: number;
  /** Whether "Select all N matching" would add rows to the selection. */
  canSelectAllMatching?: boolean;
  /** True while the search page is fetching. The total may be a transient 0 then. */
  loading: boolean;
  /** Builds the request's `selection` at the moment an action starts. */
  buildSelection: () => BulkSelectionPayload | null;
  /**
   * The schedule selection, on the custom list search only. The list props above describe the
   * lists then. Null on every other entity, which keeps "Select all N matching".
   */
  schedules?: BulkBarScheduleSelection | null;
  /** Builds the schedule selection's `selection`, on the custom list search only. */
  buildScheduleSelection?: (() => BulkSelectionPayload | null) | null;
}>(), { excludedCount: 0, total: 0, canSelectAllMatching: false, schedules: null, buildScheduleSelection: null });

const emit = defineEmits<{
  /**
   * Clear the selection. After an action starts it names the kinds the action covered, so the
   * page clears those and keeps the rest. For example, Add tag with lists and schedules ticked
   * clears the lists only. The clear button, and every entity without schedules, name none.
   */
  (e: 'clear', kinds?: MixedRunKind[]): void;
  (e: 'select-all-matching'): void;
  /** "112 schedules" was clicked: select every schedule that passes the search. */
  (e: 'select-all-matching-schedules'): void;
  /** The tracked run is final. Counts the page holds for the search may now be stale. */
  (e: 'run-finished'): void;
}>();

const SCHEDULE_SLUG = 'custom_playlist_schedule';

const capabilitiesQuery = useBulkCapabilities(() => props.entitySlug);
const capabilities = computed(() => capabilitiesQuery.data.value ?? null);
// The schedule selection's own abilities and cap. Asked for only on the custom list search.
const scheduleCapabilitiesQuery = useBulkCapabilities(() => (props.schedules ? SCHEDULE_SLUG : null));
const scheduleCapabilities = computed(() => (props.schedules ? (scheduleCapabilitiesQuery.data.value ?? null) : null));
const capabilitiesLoading = computed(
  () => capabilitiesQuery.isLoading.value || (props.schedules !== null && scheduleCapabilitiesQuery.isLoading.value),
);
const capabilitiesFailed = computed(
  () => capabilitiesQuery.isError.value || (props.schedules !== null && scheduleCapabilitiesQuery.isError.value),
);

const runTracker = useBulkActionRun();
const run = runTracker.run;
const mixedRun = useMixedBulkRun(runTracker);

// Once per run, when it ends. Watching the id as well as the status means the second run of an
// action on lists and schedules says so too.
watch(
  () => (runTracker.isFinished.value ? run.value?.id : null),
  (finishedRunId) => {
    if (finishedRunId != null) emit('run-finished');
  },
);

/** Whether any of this entity's rows are ticked. On the custom list search these are the lists. */
const hasSelection = computed(() => props.count > 0);
const scheduleCount = computed(() => props.schedules?.count ?? 0);
const hasScheduleSelection = computed(() => scheduleCount.value > 0);
/** Whether anything of any kind is ticked. The selection row shows from the first ticked list or schedule. */
const hasAnySelection = computed(() => hasSelection.value || hasScheduleSelection.value);
const maxRows = computed(() => capabilities.value?.max_rows ?? null);
const scheduleMaxRows = computed(() => scheduleCapabilities.value?.max_rows ?? null);
/** The cap counts each kind on its own, as each kind is its own run. */
const isOverCap = computed(
  () =>
    (maxRows.value !== null && props.count > maxRows.value) ||
    (scheduleMaxRows.value !== null && scheduleCount.value > scheduleMaxRows.value),
);

/** Whether the ticked lists, or the ticked schedules, can be edited or deleted by this user. */
const editsRows = computed(
  () => hasSelection.value && Boolean(capabilities.value?.actions.update) && (capabilities.value?.fields.length ?? 0) > 0,
);
const editsSchedules = computed(
  () =>
    hasScheduleSelection.value &&
    Boolean(scheduleCapabilities.value?.actions.update) &&
    (scheduleCapabilities.value?.fields.length ?? 0) > 0,
);
const deletesRows = computed(() => hasSelection.value && Boolean(capabilities.value?.actions.delete));
const deletesSchedules = computed(() => hasScheduleSelection.value && Boolean(scheduleCapabilities.value?.actions.delete));

const canEdit = computed(() => editsRows.value || editsSchedules.value);
const canDelete = computed(() => deletesRows.value || deletesSchedules.value);
// Tags exist on lists only, so they need a ticked list.
const canAddTag = computed(() => hasSelection.value && Boolean(capabilities.value?.actions.add_tag));
const canRemoveTag = computed(() => hasSelection.value && Boolean(capabilities.value?.actions.remove_tag));
const hasAnyAction = computed(() => canEdit.value || canDelete.value || canAddTag.value || canRemoveTag.value);

const actionsDisabled = computed(
  () =>
    props.loading ||
    isOverCap.value ||
    runTracker.isActive.value ||
    mixedRun.waitingKind.value !== null ||
    !hasAnySelection.value,
);

/** The server's refusal of the last selection, such as the cap message. Cleared when the selection changes. */
const refusalMessage = ref<string | null>(null);
watch(
  () => [props.count, props.mode, props.entitySlug, scheduleCount.value],
  () => {
    refusalMessage.value = null;
  },
);

// After "select all matching" with rows unticked, the bar says how many of the matches are
// selected, so "All 104 matching" never hides the row the user took out.
const selectionText = computed(() => {
  if (props.schedules) {
    return twoKindSelectionText([
      { mode: props.mode, count: props.count, excludedCount: props.excludedCount, singular: 'list', plural: 'lists' },
      { ...props.schedules, singular: 'schedule', plural: 'schedules' },
    ]);
  }
  if (props.mode !== 'search') return `${props.count.toLocaleString()} selected`;
  if (props.excludedCount > 0) {
    return `${props.count.toLocaleString()} of ${(props.count + props.excludedCount).toLocaleString()} matching`;
  }
  return `All ${props.count.toLocaleString()} matching`;
});

/**
 * The schedule link's text in "Select all matching 40 custom lists / 112 schedules". While the
 * count is on its way it reads "… schedules", never "0 schedules", which would look like an answer.
 */
const scheduleLinkText = computed(() => {
  const schedules = props.schedules;
  if (!schedules) return '';
  if (schedules.total !== null) return `${schedules.total.toLocaleString()} schedules`;
  return schedules.totalFailed ? "schedules (couldn't count them)" : '… schedules';
});

/** The two-link offer shows while at least one of its links would add something. */
const showsTwoKindOffer = computed(
  () => props.schedules !== null && (props.canSelectAllMatching || props.schedules.canSelectAllMatching || props.schedules.total === null),
);

const capText = computed(() =>
  maxRows.value === null ? '' : `Narrow your search to ${maxRows.value.toLocaleString()} or fewer`,
);

// --- Starting an action ---
const authStore = useAuthStore();
/**
 * Where the bulk edit remembers its last edit: per entity, and per signed-in user, so the next
 * person on this browser never sees it. Before the user has loaded, nothing is remembered.
 */
const editMemoryKey = computed(() => {
  const userId = authStore.userData?.id;
  return userId === undefined ? null : `user-${userId}:${props.entitySlug}`;
});
const editOpen = ref(false);
const tagOpen = ref(false);
const tagAction = ref<'add_tag' | 'remove_tag'>('add_tag');
const confirmOpen = ref(false);
const reportOpen = ref(false);

const pendingEntitySlug = ref(props.entitySlug);
const pendingSelection = ref<BulkRequestSelection | null>(null);
const pendingOperation = ref<BulkOperation | null>(null);
const pendingValueLabels = ref<Record<string, string>>({});
/** The confirmation's lines for a bulk edit, as the edit dialog words them. */
const pendingSummary = ref<string[] | null>(null);
/** The kinds the pending action covers, for `clear`. Undefined on an entity without schedules. */
const pendingKinds = ref<MixedRunKind[] | undefined>(undefined);
/** True while the confirmation shows an action on lists and schedules. */
const pendingIsMixed = ref(false);

/** The kind this entity's rows are, on the custom list search. */
function rowKinds(): MixedRunKind[] | undefined {
  return props.schedules ? ['lists'] : undefined;
}

function openConfirm(
  entitySlug: string,
  selection: BulkRequestSelection | null,
  operation: BulkOperation,
  kinds: MixedRunKind[] | undefined,
  valueLabels: Record<string, string> = {},
  summary: string[] | null = null,
) {
  if (!selection || actionsDisabled.value) return;
  refusalMessage.value = null;
  pendingIsMixed.value = false;
  pendingEntitySlug.value = entitySlug;
  pendingSelection.value = selection;
  pendingOperation.value = operation;
  pendingKinds.value = kinds;
  pendingValueLabels.value = valueLabels;
  pendingSummary.value = summary;
  confirmOpen.value = true;
}

/** One action on the ticked lists and the ticked schedules. The coordinator previews both runs. */
function openMixedConfirm(parts: MixedRunPart[]) {
  if (actionsDisabled.value) return;
  refusalMessage.value = null;
  pendingIsMixed.value = true;
  pendingEntitySlug.value = props.entitySlug;
  pendingSelection.value = null;
  pendingOperation.value = parts[0]!.operation;
  pendingKinds.value = parts.map((part) => part.kind);
  pendingValueLabels.value = {};
  pendingSummary.value = parts.some((part) => part.summary) ? parts.flatMap((part) => part.summary ?? []) : null;
  void mixedRun.preview(parts);
  confirmOpen.value = true;
}

/** Delete on the custom list search: the ticked lists, the ticked schedules, or both. */
function startDelete() {
  if (!props.schedules) {
    // The selection is read now, never earlier, so "all matching" carries the total on screen.
    openConfirm(props.entitySlug, props.buildSelection(), { action: 'delete' }, undefined);
    return;
  }
  const listSelection = deletesRows.value ? props.buildSelection() : null;
  const scheduleSelection = deletesSchedules.value ? (props.buildScheduleSelection?.() ?? null) : null;
  if (listSelection && scheduleSelection) {
    const withoutLists = toWithoutListsSelection(listSelection);
    openMixedConfirm([
      { kind: 'lists', entitySlug: props.entitySlug, selection: listSelection, operation: { action: 'delete' } },
      {
        kind: 'schedules',
        entitySlug: SCHEDULE_SLUG,
        selection: withoutLists ? { ...scheduleSelection, without_lists: withoutLists } : scheduleSelection,
        operation: { action: 'delete' },
      },
    ]);
    return;
  }
  if (scheduleSelection) openConfirm(SCHEDULE_SLUG, scheduleSelection, { action: 'delete' }, ['schedules']);
  else openConfirm(props.entitySlug, listSelection, { action: 'delete' }, rowKinds());
}

function onEditSubmit(payload: { operation: BulkOperation; valueLabels: Record<string, string>; summary: string[] }) {
  editOpen.value = false;
  openConfirm(props.entitySlug, props.buildSelection(), payload.operation, undefined, payload.valueLabels, payload.summary);
}

/** The custom list search's edit dialog: a list edit, a schedule edit, or both. */
function onTwoKindEditSubmit(payload: { schedules: BulkEditPayload | null; lists: BulkEditPayload | null }) {
  editOpen.value = false;
  const listSelection = payload.lists ? props.buildSelection() : null;
  const scheduleSelection = payload.schedules ? (props.buildScheduleSelection?.() ?? null) : null;
  if (payload.lists && payload.schedules && listSelection && scheduleSelection) {
    openMixedConfirm([
      { kind: 'lists', entitySlug: props.entitySlug, selection: listSelection, ...payload.lists },
      { kind: 'schedules', entitySlug: SCHEDULE_SLUG, selection: scheduleSelection, ...payload.schedules },
    ]);
    return;
  }
  if (payload.schedules) {
    openConfirm(SCHEDULE_SLUG, scheduleSelection, payload.schedules.operation, ['schedules'], payload.schedules.valueLabels, payload.schedules.summary);
  } else if (payload.lists) {
    openConfirm(props.entitySlug, listSelection, payload.lists.operation, rowKinds(), payload.lists.valueLabels, payload.lists.summary);
  }
}

function onTagSubmit(payload: { operation: BulkOperation; valueLabels: Record<string, string> }) {
  tagOpen.value = false;
  openConfirm(props.entitySlug, props.buildSelection(), payload.operation, rowKinds(), payload.valueLabels);
}

function openTagDialog(action: 'add_tag' | 'remove_tag') {
  tagAction.value = action;
  tagOpen.value = true;
}

const operationSummary = computed<string[]>(() => {
  const operation = pendingOperation.value;
  if (!operation || operation.action !== 'update') return [];
  if (pendingSummary.value !== null) return pendingSummary.value;
  const fields = new Map((capabilities.value?.fields ?? []).map((field) => [field.field, field]));
  return operation.changes.map((change) =>
    describeBulkChange(change, fields.get(change.field), pendingValueLabels.value[change.field]),
  );
});

function onStarted(executedRun: BulkRun, schedulesBeforeRun: SchedulesBeforeRun | undefined) {
  runTracker.start(executedRun, pendingValueLabels.value, undefined, schedulesBeforeRun);
  // The rows are about to change, so the selection no longer describes what the user sees.
  emit('clear', pendingKinds.value);
}

function onMixedStarted(kinds: MixedRunKind[]) {
  emit('clear', kinds);
}

function onRefused(message: string) {
  refusalMessage.value = message;
}

// --- The run ---
const runNoun = computed(() => (run.value?.entity === 'location_zone_custom_playlist' ? 'lists' : 'schedules'));

const runProgressText = computed(() => {
  const progress = run.value?.progress;
  if (!run.value || !progress) return 'Starting…';
  if (run.value.status === 'queued') return `Waiting to start on ${progress.total.toLocaleString()} ${runNoun.value}…`;
  return `${progress.processed.toLocaleString()} of ${progress.total.toLocaleString()} ${runNoun.value} done`;
});

function summaryOf(finishedRun: BulkRun): string {
  if (finishedRun.status === 'failed') return `The bulk action stopped before it finished. ${bulkRunDoneSummary(finishedRun)}`;
  const failed = bulkRunFailedCount(finishedRun);
  return failed > 0
    ? `${bulkRunDoneSummary(finishedRun)} ${failed.toLocaleString()} could not be changed.`
    : bulkRunDoneSummary(finishedRun);
}

/** The finished run's summary, after the summary of the run before it in the same action. */
const runSummaryText = computed(() => {
  if (!run.value) return '';
  const previousRun = runTracker.previousRun.value;
  return previousRun ? `${summaryOf(previousRun)} ${summaryOf(run.value)}` : summaryOf(run.value);
});

/** What the bar says while the second run of an action waits to start, or could not start. */
const nextRunText = computed(() => {
  if (mixedRun.nextRunMessage.value) return mixedRun.nextRunMessage.value;
  return mixedRun.waitingKind.value ? `Starting on the ${mixedRun.waitingKind.value} next…` : '';
});

function dismissRun() {
  reportOpen.value = false;
  mixedRun.dismissPending();
  runTracker.dismiss();
}

const isVisible = computed(() => hasAnySelection.value || run.value !== null);
</script>

<template>
  <div
    v-if="isVisible"
    class="bg-background/95 supports-[backdrop-filter]:bg-background/80 border-border/80 sticky top-0 z-10 mx-3 mt-3 flex flex-col gap-2 rounded-lg border px-3 py-2 backdrop-blur"
    data-testid="bulk-action-bar"
  >
    <div v-if="hasAnySelection" class="flex flex-wrap items-center gap-2">
      <button
        type="button"
        class="text-muted-foreground hover:text-foreground hover:bg-muted/40 border-border/60 inline-flex size-6 shrink-0 items-center justify-center rounded-md border"
        data-testid="bulk-action-clear"
        aria-label="Clear selection"
        @click="emit('clear')"
      >
        <XMarkIcon class="size-3.5" aria-hidden="true" />
      </button>

      <span class="text-foreground text-sm font-medium" data-testid="bulk-action-count">{{ selectionText }}</span>
      <span
        v-if="showsTwoKindOffer && schedules"
        class="text-muted-foreground text-xs"
        data-testid="bulk-select-all-matching-row"
      >
        Select all matching
        <button
          type="button"
          class="hover:text-foreground underline-offset-2 hover:underline disabled:no-underline disabled:opacity-70"
          :disabled="!canSelectAllMatching"
          data-testid="bulk-select-all-matching-lists"
          @click="emit('select-all-matching')"
        >{{ total.toLocaleString() }} custom lists</button>
        /
        <button
          type="button"
          class="hover:text-foreground underline-offset-2 hover:underline disabled:no-underline disabled:opacity-70"
          :disabled="!schedules.canSelectAllMatching"
          data-testid="bulk-select-all-matching-schedules"
          @click="emit('select-all-matching-schedules')"
        >{{ scheduleLinkText }}</button>
      </span>
      <button
        v-else-if="canSelectAllMatching && !schedules"
        type="button"
        class="text-muted-foreground hover:text-foreground text-xs underline-offset-2 hover:underline"
        data-testid="bulk-select-all-matching"
        @click="emit('select-all-matching')"
      >
        Select all {{ total.toLocaleString() }} matching
      </button>

      <div class="ml-auto flex flex-wrap items-center gap-1.5">
        <span v-if="capabilitiesLoading" class="text-muted-foreground text-xs">Checking what you can do…</span>
        <span
          v-else-if="capabilitiesFailed"
          class="text-destructive text-xs"
          data-testid="bulk-action-capabilities-error"
        >
          Couldn't load the bulk actions.
        </span>
        <span v-else-if="!hasAnyAction" class="text-muted-foreground text-xs" data-testid="bulk-action-none">
          You can't bulk change these.
        </span>
        <template v-else>
          <Button
            v-if="canEdit"
            size="sm"
            variant="outline"
            :disabled="actionsDisabled"
            data-testid="bulk-action-edit"
            @click="editOpen = true"
          >
            Bulk edit
          </Button>
          <Button
            v-if="canAddTag"
            size="sm"
            variant="outline"
            :disabled="actionsDisabled"
            data-testid="bulk-action-add-tag"
            @click="openTagDialog('add_tag')"
          >
            Add tag
          </Button>
          <Button
            v-if="canRemoveTag"
            size="sm"
            variant="outline"
            :disabled="actionsDisabled"
            data-testid="bulk-action-remove-tag"
            @click="openTagDialog('remove_tag')"
          >
            Remove tag
          </Button>
          <Button
            v-if="canDelete"
            size="sm"
            variant="outline"
            class="text-destructive"
            :disabled="actionsDisabled"
            data-testid="bulk-action-delete"
            @click="startDelete"
          >
            Delete
          </Button>
        </template>
      </div>
    </div>

    <p v-if="hasAnySelection && isOverCap" class="text-destructive text-xs" data-testid="bulk-action-over-cap">
      {{ capText }}
    </p>
    <p v-else-if="hasAnySelection && refusalMessage" class="text-destructive text-xs" data-testid="bulk-action-refused">
      {{ refusalMessage }}
    </p>

    <div v-if="run && runTracker.isActive.value" class="flex flex-col gap-1" data-testid="bulk-action-progress">
      <div class="text-muted-foreground flex items-center justify-between text-xs">
        <span>{{ runProgressText }}</span>
        <span>{{ runTracker.progressPercent.value }}%</span>
      </div>
      <Progress :model-value="runTracker.progressPercent.value" class="h-1.5" />
    </div>

    <div
      v-else-if="run && runTracker.isFinished.value && mixedRun.waitingKind.value"
      class="flex flex-wrap items-center gap-2 text-sm"
      data-testid="bulk-action-next-run"
    >
      <span>{{ summaryOf(run) }}</span>
      <span :class="mixedRun.nextRunMessage.value ? 'text-destructive' : 'text-muted-foreground'">{{ nextRunText }}</span>
      <div v-if="mixedRun.canRunAgain.value" class="ml-auto flex items-center gap-1.5">
        <Button size="sm" variant="outline" data-testid="bulk-action-run-again" @click="mixedRun.runAgain()">
          Run it again
        </Button>
        <Button size="sm" variant="ghost" data-testid="bulk-action-dismiss-run" @click="dismissRun">Dismiss</Button>
      </div>
    </div>

    <div
      v-else-if="run && runTracker.isFinished.value"
      class="flex flex-wrap items-center gap-2 text-sm"
      data-testid="bulk-action-finished"
    >
      <span :class="run.status === 'failed' ? 'text-destructive' : ''">{{ runSummaryText }}</span>
      <span v-if="mixedRun.nextRunMessage.value" class="text-destructive" data-testid="bulk-action-next-run-failed">
        {{ mixedRun.nextRunMessage.value }}
      </span>
      <span class="text-muted-foreground text-xs">{{ BULK_RUN_CATCH_UP_NOTE }}</span>
      <div class="ml-auto flex items-center gap-1.5">
        <Button size="sm" variant="outline" data-testid="bulk-action-view-report" @click="reportOpen = true">
          View report
        </Button>
        <Button size="sm" variant="ghost" data-testid="bulk-action-dismiss-run" @click="dismissRun">Dismiss</Button>
      </div>
    </div>

    <!-- On the custom list search, schedules are edited with the schedule editor's own form, one
         tab per kind, and the lists with a "Custom Lists" tab of field rows. A tab with nothing
         ticked, or that this user may not edit, is not shown. -->
    <ScheduleBulkEditDialog
      v-if="canEdit && schedules"
      v-model:open="editOpen"
      :count="editsSchedules ? schedules.count : 0"
      :build-selection="buildScheduleSelection ?? (() => null)"
      :list-count="editsRows ? count : 0"
      :list-fields="capabilities?.fields ?? []"
      :list-memory-key="editMemoryKey"
      @submit="onTwoKindEditSubmit"
    />
    <BulkEditDialog
      v-else-if="canEdit"
      v-model:open="editOpen"
      :fields="capabilities?.fields ?? []"
      :count="count"
      :memory-key="editMemoryKey"
      @submit="onEditSubmit"
    />
    <BulkTagDialog
      v-if="canAddTag || canRemoveTag"
      v-model:open="tagOpen"
      :action="tagAction"
      :count="count"
      @submit="onTagSubmit"
    />
    <BulkConfirmDialog
      v-model:open="confirmOpen"
      :entity-slug="pendingEntitySlug"
      :selection="pendingSelection"
      :operation="pendingOperation"
      :operation-summary="operationSummary"
      :mixed-run="pendingIsMixed ? mixedRun : null"
      :snapshot-before-execute="runTracker.snapshotBeforeExecute"
      @started="onStarted"
      @mixed-started="onMixedStarted"
      @refused="onRefused"
    />
    <BulkRunReport
      v-if="run && runTracker.isFinished.value"
      v-model:open="reportOpen"
      :run="run"
      :previous-run="runTracker.previousRun.value"
    />
  </div>
</template>
